import { useCallback, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@charphq/ui/components/base/button";
import { Card, CardContent, CardHeader, CardTitle } from "@charphq/ui/components/base/card";
import { Badge } from "@charphq/ui/components/base/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@charphq/ui/components/base/tabs";
import { ArrowLeft, Thermometer, Droplets, Sun, Trash2, Pencil } from "lucide-react";
import { format } from "date-fns";
import {
  CalibrationChart,
  type Metric as CalibrationMetric,
} from "@charphq/ui/components/calibration/CalibrationChart";
import { DeviceSelector } from "@charphq/ui/components/calibration/DeviceSelector";
import { PhaseDialog } from "@charphq/ui/components/calibration/PhaseDialog";
import { CalibrationEquationPanel } from "@charphq/ui/components/calibration/CalibrationEquationPanel";
import { CalibrationReviewPanel } from "@charphq/ui/components/calibration/CalibrationReviewPanel";
import { PhaseDetectionPanel } from "@charphq/ui/components/calibration/PhaseDetectionPanel";
import { BulkDeviceAdd } from "@charphq/ui/components/calibration/BulkDeviceAdd";
import { ManualCalibrationPanel } from "@charphq/ui/components/calibration/ManualCalibrationPanel";
import { CalibrationEditDialog } from "@charphq/ui/components/calibration/CalibrationDialogs";
import {
  getGetApiCalibrationsIdQueryKey,
  useGetApiCalibrationsId,
  useDeleteApiCalibrationsId,
  type GetApiCalibrationsId200,
  type GetApiCalibrationsId200Data,
  type PostApiCalibrationsIdReviewBody,
} from "@charphq/api-client";

type Calibration = GetApiCalibrationsId200Data;

function CalibrationHeader({ calibration }: { calibration: Calibration }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const deleteMutation = useDeleteApiCalibrationsId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calibrations"] });
        navigate({ to: "/calibrations" });
      },
    },
  });

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to delete this calibration? This action cannot be undone.",
      )
    ) {
      deleteMutation.mutate({ id: calibration.id });
    }
  };

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/calibrations">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{calibration.name}</h1>
          {calibration.description && (
            <p className="text-muted-foreground mt-1">{calibration.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={calibration.endAt ? "secondary" : "default"}>
          {calibration.endAt ? "Completed" : "Active"}
        </Badge>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      </div>
      <CalibrationEditDialog
        key={editOpen ? "edit-open" : "edit-closed"}
        calibration={calibration}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={() => {
          queryClient.invalidateQueries({
            queryKey: getGetApiCalibrationsIdQueryKey(calibration.id),
          });
          queryClient.invalidateQueries({ queryKey: ["calibrations"] });
        }}
      />
    </div>
  );
}

function CalibrationOverview({ calibration }: { calibration: Calibration }) {
  const dutCount = calibration.devices.filter((d) => d.role === "dut").length;
  const refCount = calibration.devices.filter((d) => d.role === "reference").length;
  const activePhases = calibration.phases.length;

  const metrics = [];
  if (calibration.phases.some((p) => p.includeTemperature)) metrics.push("Temperature");
  if (calibration.phases.some((p) => p.includeHumidity)) metrics.push("Humidity");
  if (calibration.phases.some((p) => p.includeLux)) metrics.push("Lux");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Duration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {calibration.endAt
              ? `${Math.ceil((new Date(calibration.endAt).getTime() - new Date(calibration.startAt).getTime()) / (1000 * 60 * 60 * 24))} days`
              : `${Math.ceil((Date.now() - new Date(calibration.startAt).getTime()) / (1000 * 60 * 60 * 24))} days`}
          </div>
          <p className="text-xs text-muted-foreground">
            Started {format(new Date(calibration.startAt), "MMM d, yyyy")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Devices</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{dutCount + refCount}</div>
          <p className="text-xs text-muted-foreground">
            {dutCount} DUTs, {refCount} references
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Phases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activePhases}</div>
          <p className="text-xs text-muted-foreground">Active phases</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            {metrics.includes("Temperature") && (
              <div className="flex items-center gap-1 text-temp">
                <Thermometer className="h-4 w-4" />
              </div>
            )}
            {metrics.includes("Humidity") && (
              <div className="flex items-center gap-1 text-humidity">
                <Droplets className="h-4 w-4" />
              </div>
            )}
            {metrics.includes("Lux") && (
              <div className="flex items-center gap-1 text-light">
                <Sun className="h-4 w-4" />
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{metrics.join(", ")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Tab, focused device and metric move together: clicking a device in the Review tab
 * opens the Charts tab already focused on it. One piece of state updated by one
 * setter, so the hand-off cannot produce a render where the tab has switched but the
 * focus has not — the re-render cascade class of bug ADR 0011 and OTO-158 cover.
 */
interface CalibrationView {
  tab: string;
  focusDeviceId: string | null;
  metric: CalibrationMetric | undefined;
  /** Parameters of the last review run, so the chart's "Proposed" overlay matches it. */
  reviewParams: PostApiCalibrationsIdReviewBody | null;
}

function CalibrationDetail() {
  const { calibrationId } = Route.useParams();
  const queryClient = useQueryClient();
  const [view, setView] = useState<CalibrationView>({
    tab: "review",
    focusDeviceId: null,
    metric: undefined,
    reviewParams: null,
  });

  const inspectDevice = useCallback((deviceId: string, metric: CalibrationMetric) => {
    setView((previous) => ({ ...previous, tab: "charts", focusDeviceId: deviceId, metric }));
  }, []);

  const clearFocus = useCallback(() => {
    setView((previous) => ({ ...previous, focusDeviceId: null }));
  }, []);

  const rememberReviewParams = useCallback((params: PostApiCalibrationsIdReviewBody) => {
    setView((previous) => ({ ...previous, reviewParams: params }));
  }, []);

  const {
    data: calibrationResponse,
    isLoading,
    error,
  } = useGetApiCalibrationsId(calibrationId, {
    query: {
      enabled: !!calibrationId,
      staleTime: 30000, // 30 seconds
    },
  });

  const calibration = (calibrationResponse as GetApiCalibrationsId200 | undefined)?.data;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded animate-pulse" />
          ))}
        </div>
        <div className="h-96 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (error || !calibration) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive mb-4">Failed to load calibration</p>
        <Button asChild>
          <Link to="/calibrations">Back to Calibrations</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CalibrationHeader calibration={calibration} />
      <CalibrationOverview calibration={calibration} />

      <Tabs
        value={view.tab}
        onValueChange={(tab) => setView((previous) => ({ ...previous, tab }))}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="review">Review</TabsTrigger>
          <TabsTrigger value="manual">Manual</TabsTrigger>
          <TabsTrigger value="charts">Charts</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="phases">Phases</TabsTrigger>
          <TabsTrigger value="calibration">Calibration</TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="space-y-4">
          <CalibrationReviewPanel
            calibrationId={calibration.id}
            runStartAt={calibration.startAt}
            phases={(calibration.phases ?? []).map((phase) => ({
              id: phase.id,
              name: phase.name,
              startAt: phase.startAt,
              endAt: phase.endAt,
            }))}
            onInspectDevice={inspectDevice}
            onReviewApplied={rememberReviewParams}
          />
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <ManualCalibrationPanel calibrationId={calibration.id} runStartAt={calibration.startAt} />
        </TabsContent>

        <TabsContent value="charts" className="space-y-4">
          <CalibrationChart
            calibrationId={calibration.id}
            focusDeviceId={view.focusDeviceId}
            initialMetric={view.metric}
            onClearFocus={clearFocus}
            reviewParams={view.reviewParams}
          />
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <BulkDeviceAdd calibrationId={calibration.id} />
          <DeviceSelector calibrationId={calibration.id} />
        </TabsContent>

        <TabsContent value="phases" className="space-y-4">
          <PhaseDetectionPanel
            calibrationId={calibration.id}
            onPhasesCreated={() => {
              queryClient.invalidateQueries();
            }}
          />
          <PhaseDialog calibrationId={calibration.id} />
        </TabsContent>

        <TabsContent value="calibration" className="space-y-4">
          <CalibrationEquationPanel calibrationId={calibration.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const Route = createFileRoute("/calibrations/$calibrationId")({
  component: CalibrationDetail,
});
